import {
  LEFT_BAR_WIDTH,
  RIDE_DRAWER_DEFAULT_FORM_WIDTH,
  RIDE_DRAWER_DEFAULT_MAP_WIDTH,
  RIDE_DRAWER_TOTAL_HANDLE_WIDTH,
  RideCrudModeKeyTuple,
} from "../constants";
import { EditFormMode } from "../enums";
import {
  DrawerProps,
  DrawerType,
  ICrudDrawerProps,
  TCrudEditAtomKey,
} from "../models";
import { normalizePositiveIntId } from "./numbers.helpers";

export const serializedOpenedDrawersDependency = (
  drawers: {
    drawerProps: Partial<ICrudDrawerProps & DrawerProps<DrawerType>>;
  }[],
): string =>
  drawers
    .map(x => {
      const dp = x.drawerProps as ICrudDrawerProps & DrawerProps<DrawerType>;
      return `${dp.type}_${dp.id ?? ""}_${dp.idCopyFrom ?? ""}_${dp.mode ?? ""}`;
    })
    ?.join(",") ?? "";

export const getRideDrawerMaxContentWidth = (): number =>
  typeof window !== "undefined"
    ? window.innerWidth - LEFT_BAR_WIDTH
    : RIDE_DRAWER_DEFAULT_FORM_WIDTH +
      RIDE_DRAWER_DEFAULT_MAP_WIDTH +
      RIDE_DRAWER_TOTAL_HANDLE_WIDTH;

export const drawerUnboundOrExplicitMode = (
  props: DrawerProps<DrawerType>,
): EditFormMode =>
  "mode" in props && props.mode !== undefined
    ? props.mode
    : EditFormMode.UnboundDrawer;

/**
 * `editFormDataAF` / snapshot / loading keys used by `CrudEditContextWrapper` per drawer type.
 * Ride keeps `RideCrudModeKeyTuple` (ride + fixed form mode); other CRUD drawers use UnboundDrawer by default.
 */
export const resolveCrudEditDrawerAtomKey = (
  props: DrawerProps<DrawerType>,
): TCrudEditAtomKey | null => {
  const id = normalizePositiveIntId((props as ICrudDrawerProps).id);
  const idCopyFrom = normalizePositiveIntId(
    (props as ICrudDrawerProps).idCopyFrom,
  );

  if (!idCopyFrom && !id) {
    return null;
  }

  switch (props.type) {
    case DrawerType.RideEditForm:
      return [...RideCrudModeKeyTuple];
    case DrawerType.BookingEditForm:
      return ["booking", drawerUnboundOrExplicitMode(props)];
    case DrawerType.CourseEditForm:
      return ["course", drawerUnboundOrExplicitMode(props)];
    case DrawerType.JourneyEditForm:
      return ["journey", drawerUnboundOrExplicitMode(props)];
    case DrawerType.StationRouteEditForm:
      return ["stationRoute", drawerUnboundOrExplicitMode(props)];
    case DrawerType.NotificationEditForm:
      return ["notification", drawerUnboundOrExplicitMode(props)];
    case DrawerType.EditForm: {
      if (!("entity" in props) || typeof props.entity !== "string") {
        return null;
      }
      if (props.entity === "unknown") {
        return null;
      }
      const mode = drawerUnboundOrExplicitMode(props);
      if (mode === EditFormMode.DropdownAddDrawer) {
        return null;
      }
      return [props.entity, mode];
    }
    default:
      return null;
  }
};
